/**
 * Document CRDT (Conflict-free Replicated Data Type) Implementation
 *
 * Implements field-level Last-Write-Wins (LWW) CRDT for document synchronization.
 * Enables conflict-free merging of concurrent edits across multiple clients/devices.
 *
 * Key Features:
 * - Field-level granularity: Different fields can be edited concurrently without conflicts
 * - Last-Write-Wins: Conflicts resolved automatically using HLC timestamps
 * - No data loss: All concurrent edits are preserved when editing different fields
 * - Deterministic: Same input always produces same output (commutative, idempotent)
 *
 * Architecture:
 * - Documents store form data in `super_form` (flat key-value structure)
 * - Each field has CRDT metadata with HLC timestamp in `super_form_meta`
 * - Merge algorithm compares timestamps to resolve conflicts
 *
 * Use Cases:
 * - Offline editing: Merge local changes with server updates when back online
 * - Real-time collaboration: Multiple users editing same document simultaneously
 * - Multi-device sync: User edits document on phone, then laptop
 *
 * @module document-crdt
 */

import { isEqual } from 'lodash';
import type { SuperFormCRDTMetadata } from '@/db/types/document';
import { HybridLogicalClockService } from './hlc';

/**
 * Result of merging two document versions
 *
 * Contains the merged document data, metadata, and statistics about the merge operation.
 * Statistics help with debugging and monitoring conflict resolution.
 *
 * @interface MergeResult
 * @property {Record<string, unknown>} merged_form - Merged document form data (field values)
 * @property {SuperFormCRDTMetadata} merged_meta - Merged CRDT metadata (timestamps per field)
 * @property {number} fields_diverged - Number of fields where both sides had different values, resolved via LWW.
 *   Note: Without a base document, this counts all value differences including one-sided edits.
 * @property {number} local_wins - Number of conflicts where local version won (had later timestamp)
 * @property {number} remote_wins - Number of conflicts where remote version won (had later timestamp)
 *
 * @example
 * const result: MergeResult = {
 *   merged_form: { field1: 'local value', field2: 'remote value' },
 *   merged_meta: {
 *     field1: '1738123456789-0-client-1',
 *     field2: '1738123456790-0-client-2'
 *   },
 *   fields_diverged: 0, // No conflicts (different fields)
 *   local_wins: 1,
 *   remote_wins: 1
 * };
 */
export interface MergeResult {
  merged_form: Record<string, unknown>;
  merged_meta: SuperFormCRDTMetadata;
  fields_diverged: number;
  local_wins: number;
  remote_wins: number;
  /** Non-empty local values AUTHORED ON THIS DEVICE (the losing stamp's node is
   *  this clientUUID) that the merge threw away because the server won the
   *  conflict. Surfaced so callers can persist them (e.g. as offline analytics
   *  events) — a breadcrumb for recovering/auditing a lost edit. Values that
   *  merely propagated here from the server (stale cached copies losing to a
   *  newer online edit) are deliberately excluded — recording those would fire
   *  on every online edit × every caching device, drowning real losses in noise
   *  and shipping stale values into analytics. */
  discarded: Array<{ field: string; value: unknown; timestamp?: string }>;
}

/**
 * Merge two document versions using CRDT Last-Write-Wins semantics
 *
 * Performs a three-way field-level merge between local and remote document versions.
 * Each field is compared independently, allowing concurrent edits to different fields
 * to coexist without conflicts.
 *
 * Merge Algorithm (per field):
 * 1. **Field only in local**: Keep local value and metadata
 * 2. **Field only in remote**: Keep remote value and metadata
 * 3. **Field in both, same value**: Keep value, use latest metadata
 * 4. **Field in both, different values (CONFLICT)**:
 *    - Compare HLC timestamps from metadata
 *    - Keep value with later timestamp (Last-Write-Wins)
 *    - If timestamps identical, prefer local for consistency
 *
 * Conflict-Free Properties:
 * - **Idempotent**: merge(A, A) = A (merging same data is safe)
 * - **Associative**: merge(merge(A, B), C) = merge(A, merge(B, C))
 * - **Tie-break**: On exact timestamp tie, the first argument (local) wins
 *
 * Note: Not strictly commutative due to tie-breaking — merge(A, B) may differ
 * from merge(B, A) when timestamps are identical. In practice, exact ties are
 * extremely rare (requires same millisecond, same logical counter, same UUID).
 *
 * @param {Object} local - Local document version
 * @param {Record<string, unknown>} local.super_form - Local form data (field key-value pairs)
 * @param {SuperFormCRDTMetadata} [local.super_form_meta] - Local CRDT metadata (timestamps per field)
 * @param {Object} remote - Remote document version
 * @param {Record<string, unknown>} remote.super_form - Remote form data (field key-value pairs)
 * @param {SuperFormCRDTMetadata} [remote.super_form_meta] - Remote CRDT metadata (timestamps per field)
 * @param {string} clientUUID - Current client UUID (for creating default timestamps if metadata missing)
 * @returns {MergeResult} Merged document with statistics
 *
 * @example
 * // Scenario: User A edits field1, User B edits field2 (no conflict)
 * const local = {
 *   super_form: { field1: 'edited by A', field2: 'original' },
 *   super_form_meta: {
 *     field1: '1738123456789-0-client-A',
 *     field2: '1738123450000-0-client-A'
 *   }
 * };
 * const remote = {
 *   super_form: { field1: 'original', field2: 'edited by B' },
 *   super_form_meta: {
 *     field1: '1738123450000-0-client-B',
 *     field2: '1738123456790-0-client-B'
 *   }
 * };
 * const result = mergeDocumentCRDT(local, remote, 'client-A');
 * // result.merged_form = { field1: 'edited by A', field2: 'edited by B' }
 * // result.fields_diverged = 0 (different fields, no conflict)
 *
 * @example
 * // Scenario: User A and User B both edit field1 (conflict - LWW resolution)
 * const local = {
 *   super_form: { field1: 'edited by A' },
 *   super_form_meta: { field1: '1738123456789-0-client-A' }
 * };
 * const remote = {
 *   super_form: { field1: 'edited by B' },
 *   super_form_meta: { field1: '1738123456790-0-client-B' } // Later timestamp
 * };
 * const result = mergeDocumentCRDT(local, remote, 'client-A');
 * // result.merged_form = { field1: 'edited by B' } (remote wins - later timestamp)
 * // result.fields_diverged = 1
 * // result.remote_wins = 1
 *
 * @example
 * // Scenario: Merging with legacy document (no metadata)
 * const local = {
 *   super_form: { field1: 'value' },
 *   super_form_meta: { field1: '1738123456789-0-client-A' }
 * };
 * const remote = {
 *   super_form: { field1: 'value' }
 *   // No super_form_meta (legacy document)
 * };
 * const result = mergeDocumentCRDT(local, remote, 'client-A');
 * // Default timestamp created for remote fields without metadata
 * // Local wins because it has explicit timestamp
 */
export function mergeDocumentCRDT(
  local: { super_form: Record<string, unknown>; super_form_meta?: SuperFormCRDTMetadata },
  remote: { super_form: Record<string, unknown>; super_form_meta?: SuperFormCRDTMetadata },
  clientUUID: string
): MergeResult {
  const mergedForm: Record<string, unknown> = {};
  const mergedMeta: SuperFormCRDTMetadata = {};

  // Single HLC instance for all default timestamps within this merge
  const hlc = new HybridLogicalClockService(clientUUID);

  let fieldsDiverged = 0;
  let localWins = 0;
  let remoteWins = 0;
  const discarded: MergeResult['discarded'] = [];

  // Get all unique field names
  const allFields = new Set([
    ...Object.keys(local.super_form || {}),
    ...Object.keys(remote.super_form || {})
  ]);

  for (const fieldName of allFields) {
    const localValue = local.super_form?.[fieldName];
    const remoteValue = remote.super_form?.[fieldName];
    const localTimestamp = local.super_form_meta?.[fieldName];
    const remoteTimestamp = remote.super_form_meta?.[fieldName];

    // Field only in local. Counted as a local_win on purpose: a local-only field
    // is either a genuine never-synced edit (must be pushed) or one the remote
    // payload merely omitted (permission filter on a pull, stream partition on a
    // broadcast). Those are indistinguishable here, so callers keeping the doc
    // unsynced on local_wins choose harmless re-push (idempotent) over stranding a
    // real edit — deliberate churn-over-data-loss, the whole point of this branch.
    // The value is kept, not resurrected: the offline API never drops a key for a
    // cleared field (it stays '' / null), so a local-only field never masks a
    // server deletion (only a template field removal could, which is a schema change).
    if (localValue !== undefined && remoteValue === undefined) {
      mergedForm[fieldName] = localValue;
      mergedMeta[fieldName] = localTimestamp || createDefaultTimestamp(hlc);
      localWins++;
      continue;
    }

    // Field only in remote
    if (remoteValue !== undefined && localValue === undefined) {
      mergedForm[fieldName] = remoteValue;
      mergedMeta[fieldName] = remoteTimestamp || createDefaultTimestamp(hlc);
      remoteWins++;
      continue;
    }

    // Field in both - check if values are equal
    if (isEqual(localValue, remoteValue)) {
      mergedForm[fieldName] = localValue;
      const cmp = compareTimestamps(localTimestamp, remoteTimestamp);
      // Use explicit checks instead of non-null assertions
      if (cmp >= 0 && localTimestamp) {
        mergedMeta[fieldName] = localTimestamp;
      } else if (remoteTimestamp) {
        mergedMeta[fieldName] = remoteTimestamp;
      } else {
        // Fallback if both timestamps are missing
        mergedMeta[fieldName] = createDefaultTimestamp(hlc);
      }
    } else {
      // Conflict! Use Last-Write-Wins based on timestamp
      const cmp = compareTimestamps(localTimestamp, remoteTimestamp);
      if (cmp > 0) {
        mergedForm[fieldName] = localValue;
        mergedMeta[fieldName] = localTimestamp || createDefaultTimestamp(hlc);
        localWins++;

        // Log conflict resolution (only in non-production)
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[CRDT] Conflict resolved: local wins', {
            field: fieldName,
            localValue,
            remoteValue,
            localTimestamp,
            remoteTimestamp,
            comparison: cmp
          });
        }
      } else if (cmp < 0) {
        mergedForm[fieldName] = remoteValue;
        mergedMeta[fieldName] = remoteTimestamp || createDefaultTimestamp(hlc);
        remoteWins++;

        // The local value lost and is being thrown away — record it as a
        // recovery breadcrumb, but only when it holds something AND was
        // authored on THIS device (losing stamp's node === our clientUUID).
        // A stale cached copy of someone else's edit losing to a newer online
        // value is routine propagation, not a lost edit.
        if (localValue !== undefined && localValue !== null && localValue !== '' &&
            authoredBy(localTimestamp, clientUUID)) {
          discarded.push({ field: fieldName, value: localValue, timestamp: localTimestamp });
        }

        // Log conflict resolution (only in non-production)
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[CRDT] Conflict resolved: remote wins', {
            field: fieldName,
            localValue,
            remoteValue,
            localTimestamp,
            remoteTimestamp,
            comparison: cmp
          });
        }
      } else {
        // Exact tie - prefer local for consistency
        mergedForm[fieldName] = localValue;
        mergedMeta[fieldName] = localTimestamp || createDefaultTimestamp(hlc);
        localWins++;

        // Log conflict resolution (only in non-production)
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[CRDT] Conflict resolved: tie (local preferred)', {
            field: fieldName,
            localValue,
            remoteValue,
            localTimestamp,
            remoteTimestamp,
            comparison: cmp
          });
        }
      }
      fieldsDiverged++;
    }
  }

  // Preserve confirmed_prefill:<slug> metadata. These keys have no super_form
  // field companion (allFields only covers value-bearing fields), so without this
  // the merge drops them — and confirmed_prefill is the sole signal that a
  // prefilled field was confirmed. Union both sides, keeping the later stamp on
  // overlap. Non-namespaced orphan meta (a stamp whose field is gone from
  // super_form) is intentionally NOT carried over — same as pullDocument's cleanup.
  const metaKeys = new Set([
    ...Object.keys(local.super_form_meta || {}),
    ...Object.keys(remote.super_form_meta || {})
  ]);
  for (const key of metaKeys) {
    if (allFields.has(key) || !key.startsWith('confirmed_prefill:')) continue;
    const localMetaOnly = local.super_form_meta?.[key];
    const remoteMetaOnly = remote.super_form_meta?.[key];
    const winner = compareTimestamps(localMetaOnly, remoteMetaOnly) >= 0 ? localMetaOnly : remoteMetaOnly;
    if (winner !== undefined) mergedMeta[key] = winner;
  }

  return {
    merged_form: mergedForm,
    merged_meta: mergedMeta,
    fields_diverged: fieldsDiverged,
    local_wins: localWins,
    remote_wins: remoteWins,
    discarded
  };
}

/**
 * Compare two CRDT timestamps for ordering
 *
 * Helper function to determine which timestamp is "later" for Last-Write-Wins resolution.
 * Handles cases where metadata is missing (legacy documents or new fields).
 *
 * Comparison Rules:
 * - Both missing: Remote (server) wins (return -1). An unstamped local field is
 *   NOT an authoritative CRDT edit — every real local edit is HLC-stamped when
 *   made — so it must defer to the server rather than tie-to-local (which would
 *   silently suppress remote updates to legacy/metadata-less fields). This also
 *   matches the server merge, which keeps its own value when both sides lack a stamp.
 * - Local missing: Remote wins (return -1)
 * - Remote missing: Local wins (return 1)
 * - Malformed timestamp: Treat as missing (older than valid timestamp)
 * - Both present: Compare using HLC.compare()
 *
 * @param {string | undefined} local - Local timestamp string (HLC format: "physical-logical-uuid")
 * @param {string | undefined} remote - Remote timestamp string (HLC format: "physical-logical-uuid")
 * @returns {number} Comparison result:
 *                   -1 if local is earlier (remote wins)
 *                    0 if timestamps are equal
 *                    1 if local is later (local wins)
 *
 * Also orders warning dismissals (lib/warning_dismissals), like any other field.
 * @see HybridLogicalClockService.compare for detailed comparison logic
 *
 * @example
 * const local = '1738123456789-0-client-A';
 * const remote = '1738123456790-0-client-B';
 * const cmp = compareTimestamps(local, remote);
 * // cmp = -1 (remote has later physical time)
 *
 * @example
 * // Handling missing metadata
 * const cmp1 = compareTimestamps(undefined, '1738123456789-0-client-B');
 * // cmp1 = -1 (remote wins when local has no metadata)
 *
 * const cmp2 = compareTimestamps('1738123456789-0-client-A', undefined);
 * // cmp2 = 1 (local wins when remote has no metadata)
 *
 * @example
 * // Handling malformed timestamps
 * const cmp3 = compareTimestamps('invalid', '1738123456789-0-client-B');
 * // cmp3 = -1 (remote wins when local timestamp is malformed)
 */
export function compareTimestamps(
  local: string | null | undefined,
  remote: string | null | undefined
): number {
  if (!local && !remote) return -1; // Neither is a stamped edit → server wins
  if (!local) return -1; // Remote wins
  if (!remote) return 1;  // Local wins

  try {
    const localHLC = HybridLogicalClockService.fromString(local);
    try {
      const remoteHLC = HybridLogicalClockService.fromString(remote);
      return HybridLogicalClockService.compare(localHLC, remoteHLC);
    } catch (_remoteError) {
      // Remote malformed, local wins
      return 1;
    }
  } catch (_localError) {
    // Local malformed
    try {
      HybridLogicalClockService.fromString(remote);
      // Remote is valid, remote wins
      return -1;
    } catch (_remoteError) {
      // Both malformed → neither is a valid stamped edit → server wins
      return -1;
    }
  }
}

/**
 * Create a default timestamp for a field without CRDT metadata
 *
 * Used when merging with legacy documents or when metadata is missing.
 * Creates a new HLC timestamp with current time.
 *
 * This is used during lazy migration: when old documents (without CRDT metadata)
 * are merged with new documents (with metadata), default timestamps are created
 * for the old document's fields. The current time is used, which may not reflect
 * the actual creation time, but ensures deterministic ordering going forward.
 *
 * @param {HybridLogicalClockService} hlc - HLC instance to generate timestamp from
 * @returns {string} Timestamp string in HLC format ("physical-logical-uuid")
 *
 * @private
 */
function createDefaultTimestamp(hlc: HybridLogicalClockService): string {
  return HybridLogicalClockService.toString(hlc.now());
}

/**
 * Whether an HLC timestamp string was authored by the given client (its node
 * segment matches). Missing/malformed stamps return false — an unauthored
 * value can't be a lost local edit.
 */
function authoredBy(timestamp: string | undefined, clientUUID: string): boolean {
  if (!timestamp) return false;
  try {
    return HybridLogicalClockService.fromString(timestamp).client_uuid === clientUUID;
  } catch {
    return false;
  }
}
